import { parsePhoneNumber } from 'libphonenumber-js'
import { load } from 'recaptcha-v3'
import { defineStore } from 'pinia'
import type { ReCaptchaInstance } from 'recaptcha-v3'
import countryList from '~/components/ui/country-selectbox/country.json'
import enquiryTravellerApi from '~/composables/api/enquiryTravellerApi'
import { ENQUIRY_STEP_BASE, EnquiryStepEnum, keyLocalStorage } from '~/enum/enquiry'
import { getEnquiryID, storeStepData } from '~/helper/enquiry'
import type { EnquiryMultiStepObj } from '~/models/EnquiryCapture/EnquiryMultiStepObj'
import type { Destination, EnquiryOption } from '~/models/enquiryOption.model'
import type { Gallery } from '~/models/gallery.model'
import type { UserDetail } from '~/models/user.model'

export const useEnquiryCaptureStore = defineStore('storeEnquiryCapture', {
  state: () => ({
    submitFrom: 'global' as 'global' | 'trip_plan' | 'ld_profile' | 'destination',
    loading: false,
    enquiryCaptureData: null as EnquiryMultiStepObj | null,
    userLoginData: null as UserDetail | null,
    enquiryOptionData: null as EnquiryOption | null,
    enquiryAuthenticate: null as any,
    recaptcha: null as ReCaptchaInstance | null,
    recaptcha_token: null as string | null,
    galleryData: { data: null, listDestination: [] } as { data: Gallery | null; listDestination: Array<Destination> },
    stepsData: null as any,
    arrayStep: ENQUIRY_STEP_BASE,
  }),
  getters: {
    enquiryDataByStep: (state) => {
      return (path: string, step: EnquiryStepEnum) => {
        if (state.stepsData && state.stepsData[path]) {
          return state.stepsData[path][step] || null
        }
        return null
      }
    },
  },
  actions: {
    async getEnquiryByID(id: string) {
      // get data from API
      try {
        const { data: res } = await enquiryTravellerApi.getEnquiryByID(id)
        if (res.status === 200) {
          if (res.data && res.data.step === EnquiryStepEnum.YourBudget) {
            return { completed: true }
          }
          return mapDataEnquiryMultiStep(res.data)
        }
      } catch (error) {
        console.log('EnquiryCaptureStore: func fetchEnquiryData', error)
      }
    },
    async submitEnquiryCapture({ payload, dataStep }: any) {
      const arrayStepEnquiry = this.arrayStep
      const firstStepIndex = 0
      const enquiryID = getEnquiryID(payload?.path)
      const stepsData = this.stepsData && this.stepsData[payload.path] ? this.stepsData[payload.path] : {}
      const isFinalStep = payload?.step === arrayStepEnquiry[arrayStepEnquiry.length - 1]

      let params = {
        ...stepsData?.params,
        form_token: stepsData?.form_token,
        enquiry_id: enquiryID,
        step: payload?.step || null,
        [payload?.step]: payload[payload?.step],
        req_source: payload?.req_source || undefined,
        geo_loc: payload?.geo_loc || undefined,
        contest_gtm: payload?.contest_gtm || undefined,
        redirect_url: payload?.redirect_url || undefined,
        new_version: 1,
        is_final_step: isFinalStep ? 1 : undefined,
      } as any
      if (!enquiryID || !params.form_token) {
        if (!this.recaptcha_token) {
          params['g-recaptcha-response'] = await this.getReCaptchaToken()
        } else params['g-recaptcha-response'] = this.recaptcha_token
      } else {
        delete params['g-recaptcha-response']
      }

      // async data form customise
      if (payload?.step === arrayStepEnquiry[firstStepIndex] && payload[EnquiryStepEnum.YourStyle]) {
        params = {
          ...params,
          [EnquiryStepEnum.YourStyle]: payload[EnquiryStepEnum.YourStyle],
        }
      }

      try {
        const { data: res } = await enquiryTravellerApi.submitMultiStepEnquiry(params)
        // console.log({params})
        if (res?.status === 200) {
          const next_step = arrayStepEnquiry[arrayStepEnquiry.indexOf(payload?.step) + 1]
          const form_token = res?.data?.form_token || null
          const enquiry_id = res?.data?.enquiry_id || null

          const stepsData = {
            enquiry_id: enquiry_id,
            [payload?.step]: { ...toRaw(dataStep) },
            form_token,
            current_step: payload?.step,
            next_step,
            path: payload?.path,
            params,
          }

          const sessionData = storeStepData(stepsData)
          this.setStepsData(sessionData)
          if (res?.data?.enquiry_infos) {
            this.setEnquiryCaptureData(res.data?.enquiry_infos)
          }
          if (res?.data?.authenticate) {
            this.setEnquiryAuthenticate(res.data?.authenticate)
          }
        }
        return res
      } catch (error: any) {
        console.log(error)
        return {
          message: error?.message || '',
        }
      }
    },
    removeRouteStore({ path, clearSession = false }: { path: string; clearSession: boolean }) {
      this.setEnquiryCaptureData(null)
      const enquiryMultiInfo = JSON.parse(localStorage.getItem(keyLocalStorage.enquiry)!)
      if (enquiryMultiInfo && path && enquiryMultiInfo[path]) {
        delete enquiryMultiInfo[path]
      }
      if (enquiryMultiInfo) {
        localStorage.setItem(keyLocalStorage.enquiry, JSON.stringify(enquiryMultiInfo))
        if (clearSession) sessionStorage.setItem(keyLocalStorage.enquiry, JSON.stringify(enquiryMultiInfo))
      }
      this.setStepsData(null)
      this.setEnquiryOptionData(null)
      this.setGalleryData({ data: {}, listDestination: [] })
    },
    cachingStepEnquiry(payload: { step: EnquiryStepEnum; path: string }) {
      if (!payload.step) return
      const enquiryMultiInfo = JSON.parse(localStorage.getItem(keyLocalStorage.enquiry)!)
      if (enquiryMultiInfo && enquiryMultiInfo[payload.path]) {
        enquiryMultiInfo[payload.path.toString()].step = payload.step
        localStorage.setItem(keyLocalStorage.enquiry, JSON.stringify(enquiryMultiInfo))

        const session = JSON.parse(sessionStorage.getItem(keyLocalStorage.enquiry)!)
        sessionStorage.setItem(keyLocalStorage.enquiry, JSON.stringify({ ...session, [payload.path]: { ...enquiryMultiInfo[payload.path] } }))
        if (this.stepsData && this.stepsData[payload.path]) {
          this.stepsData[payload.path].step = payload.step
        }
      }
    },
    async getReCaptchaToken() {
      try {
        let token = ''
        if (this.recaptcha) {
          token = await this.recaptcha.execute('submit')
        } else {
          const recaptcha = await load(`${useAppConfig().siteKeyGreCaptcha}`)
          token = await recaptcha.execute('submit')
          this.setRecaptcha(recaptcha)
        }
        this.setRecaptchaToken(token)
        return token
      } catch (e) {
        console.log('recaptcha error', e)
        throw new Error('recaptcha error: ', { cause: e })
      }
    },
    // Mutations
    setEnquiryCaptureData(data: EnquiryMultiStepObj | null) {
      this.enquiryCaptureData = data
    },
    setEnquiryAuthenticate(data: any) {
      this.enquiryAuthenticate = data
    },
    setStepsData(data: any) {
      this.stepsData = data
    },
    setUserLoginData(data: any) {
      this.userLoginData = data
    },
    setEnquiryOptionData(data: any) {
      this.enquiryOptionData = data
    },
    setRecaptcha(data: any) {
      this.recaptcha = data
    },
    setRecaptchaToken(data: any) {
      this.recaptcha_token = data
    },
    setGalleryData(payload: any) {
      this.galleryData.data = payload?.data
      this.galleryData.listDestination = payload?.listDestination
    },
    setArrayStep(data: EnquiryStepEnum[]) {
      this.arrayStep = data
    },
  },
})

const mapDataEnquiryMultiStep = (data: EnquiryMultiStepObj) => {
  const { enquiry_id, step, information, your_trip, how, your_style } = data
  let result = { enquiry_id, step, params: data } as any
  if (information) {
    const { phone, contact_name, check_newsletter, email } = information
    const phoneFormat = phone ? parsePhoneNumber(phone) : null
    let nationality = null
    if (phoneFormat && phoneFormat.countryCallingCode) {
      const ctr = countryList.find((item) => {
        return item.dial_code === phoneFormat.countryCallingCode
      })
      nationality = ctr ? ctr.code : null
    }
    result = {
      ...result,
      information: {
        email,
        check_newsletter: check_newsletter,
        contactName: contact_name,
        nationality,
        phone: phoneFormat?.nationalNumber || phone,
      },
    }
  }
  if (your_trip) {
    const { destinations_raw, exact_date, has_flight_ticket, length_of_trip, arrival_date, arrival_month, dates } = your_trip
    let dateSelected, ddMMMYYYY, mmmYYYY
    if (!!exact_date) {
      ddMMMYYYY = new Date(arrival_date)
      dateSelected = ddMMMYYYY?.getDate() + '-' + (ddMMMYYYY!.getMonth() + 1) + '-' + ddMMMYYYY?.getFullYear()
    } else {
      mmmYYYY = dates
      dateSelected = arrival_month
    }
    const lengthOfTrip = length_of_trip.replaceAll('days', '').replaceAll('day', '').trim()
    result = {
      ...result,
      your_trip: {
        destination: destinations_raw.map((v) => ({ ...v, id: v.city_id, country_code: v.country_id })),
        exactDate: !!exact_date,
        haveFlights: has_flight_ticket ? 'yes' : 'no',
        lengthOfTrip: lengthOfTrip !== '0' ? lengthOfTrip : null,
        ddMMMYYYY: ddMMMYYYY || null,
        mmmYYYY: mmmYYYY || null,
        dateSelected: dateSelected || null,
      },
    }
  }
  if (how) {
    const { accm_pref, ideal_room_config, pax } = how
    result = {
      ...result,
      how: {
        accm_pref: accm_pref ? accm_pref.split(',') : [],
        ideal_room_config,
        pax,
      },
    }
  }
  if (your_style) {
    const { suggestion } = your_style
    result = {
      ...result,
      your_style: {
        suggestion: suggestion || '',
      },
    }
  }
  return result
}
